import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Staff from "../../components/Staff";
import Loca from "../../components/Loca";
import InlineLocationSelect from "../../components/InlineLocationSelect";
import ClickAwayListener from "../../../topbar/components/ClickAwayListener";
import { ChevronRight, User, MapPin, Layers, Users, Edit3 } from "lucide-react";
import { usePrimaryColor } from "../../components/PrimaryColorContext";

const LEVELS = ["Beginner", "Intermediate", "Advanced"];

/**
 * ClassOverview Component
 * Displays class overview information with class name header.
 * Includes managed by location and clickable capacity for editing.
 * Compact design for horizontal card layout.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.htClass - The class object
 * @param {Function} props.setHtClass - Function to update class object
 *
 * @author Forge
 * @date 2026-04-10
 * @version 4.1.0
 */
export default function ClassOverview({ htClass, setHtClass }) {
  const navigate = useNavigate();
  const { primaryColor } = usePrimaryColor();
  const [isEditingCapacity, setIsEditingCapacity] = useState(false);
  const [isEditingLevel, setIsEditingLevel] = useState(false);

  const handleLevelSelect = useCallback(
    (level) => {
      setHtClass({ ...htClass, level });
      setIsEditingLevel(false);
    },
    [htClass, setHtClass]
  );

  const handleLevelClickAway = useCallback(() => {
    setIsEditingLevel(false);
  }, []);

  const statusPercent = Math.min(
    (htClass.students.length / htClass.maxStudents) * 100,
    100
  );

  const handleManagedByChange = (managedBy) => {
    setHtClass({ ...htClass, managedBy });
  };

  const handleMaxStudentsChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value > 0) {
      setHtClass({ ...htClass, maxStudents: value });
    }
  };

  const handleCapacityClick = () => {
    setIsEditingCapacity(true);
  };

  const handleCapacityBlur = () => {
    setIsEditingCapacity(false);
  };

  return (
    <div className="flex-1 min-w-[280px] bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="px-4 py-4 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center gap-2 mb-1.5">
          <h1 className="text-2xl font-black text-slate-900 tracking-tight leading-none">
            {htClass.name}
          </h1>
        </div>
        <div className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider">
            <button
              className={`hover:text-${primaryColor}-800 transition-colors cursor-pointer`}
            onClick={() => navigate("/manage/classes")}
          >
            Classes
          </button>
            <ChevronRight size={10} className="text-slate-300" />
          <span className={`text-${primaryColor}-800`}>{htClass.name}</span>
        </div>
      </div>

      {/* Info Rows */}
      <div className="p-4 flex flex-col gap-3">
        {/* Teacher */}
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
            <User size={14} />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[0.65rem] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">
              Teacher
            </span>
            <div className="font-bold text-slate-800 text-sm truncate">
              {htClass.teacher ? <Staff id={htClass.teacher} /> : "No teacher"}
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
            <MapPin size={14} />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[0.65rem] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">
              Location
            </span>
            <div className="font-bold text-slate-800 text-sm truncate">
              <Loca id={htClass.location} />
            </div>
          </div>
        </div>

        {/* Level */}
        <ClickAwayListener onClickAway={handleLevelClickAway}>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
              <Layers size={14} />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[0.65rem] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">
                Level
              </span>
              {isEditingLevel ? (
                <div className="flex items-center gap-1 mt-0.5">
                  {LEVELS.map((lvl) => (
                    <button
                      key={lvl}
                      onClick={() => handleLevelSelect(lvl)}
                      className={`text-[0.65rem] font-bold uppercase tracking-tight px-2 py-0.5 rounded-full border transition-colors cursor-pointer ${
                        htClass.level === lvl
                          ? `bg-${primaryColor}-800 text-white border-${primaryColor}-800`
                          : `bg-white text-slate-600 border-slate-200 hover:border-${primaryColor}-800 hover:text-${primaryColor}-800`
                      }`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
              ) : (
                <div>
                  <button
                    onClick={() => setIsEditingLevel(true)}
                    className={`bg-${primaryColor}-50 text-${primaryColor}-900 text-xs px-2 py-0.5 rounded-full border border-${primaryColor}-50 font-bold uppercase tracking-tight cursor-pointer hover:bg-${primaryColor}-50 hover:border-${primaryColor}-50 transition-colors`}
                    title="Click to change level"
                  >
                    {htClass.level || "Not set"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </ClickAwayListener>

        {/* Managed By */}
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
            <Users size={14} />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[0.65rem] font-black text-slate-400 uppercase tracking-wider leading-none mb-0.5">
              Managed By
            </span>
            <InlineLocationSelect
              value={htClass.managedBy}
              setValue={handleManagedByChange}
              size="small"
            />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-slate-50 border border-slate-100 p-2.5 rounded-xl flex flex-col items-center justify-center text-center">
            <div className="text-lg font-black text-slate-900 leading-none">
              {htClass.students.length}
            </div>
            <div className="text-[0.6rem] font-black text-slate-400 uppercase tracking-wider mt-0.5">
              Students
            </div>
          </div>

          <div
            className={`p-2.5 rounded-xl flex flex-col items-center justify-center text-center border transition-colors ${
              htClass.pendingStudents.length > 0
                ? "bg-amber-50 border-amber-100"
                : "bg-slate-50 border-slate-100"
            }`}
          >
            <div
              className={`text-lg font-black leading-none ${
                htClass.pendingStudents.length > 0
                  ? "text-amber-700"
                  : "text-slate-900"
              }`}
            >
              {htClass.pendingStudents.length}
            </div>
            <div
              className={`text-[0.6rem] font-black uppercase tracking-wider mt-0.5 ${
                htClass.pendingStudents.length > 0
                  ? "text-amber-600"
                  : "text-slate-400"
              }`}
            >
              Pending
            </div>
          </div>
        </div>
      </div>

      {/* Capacity Bar - Clickable to edit */}
      <div className="px-4 pb-3">
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-[0.65rem] font-black text-slate-400 uppercase tracking-wider">
            Capacity
          </span>
          {isEditingCapacity ? (
            <input
              type="number"
              value={htClass.maxStudents}
              onChange={handleMaxStudentsChange}
              onBlur={handleCapacityBlur}
              autoFocus
              className={`w-16 text-xs font-black text-slate-700 bg-white border border-${primaryColor}-800 rounded px-2 py-1 text-right focus:outline-none focus:ring-2 focus:ring-${primaryColor}-50`}
              min="1"
              step="1"
            />
          ) : (
            <div
              className={`flex items-center gap-1 text-xs font-black text-slate-700 cursor-pointer hover:text-${primaryColor}-800 transition-colors group`}
              onClick={handleCapacityClick}
              title="Click to edit capacity"
            >
              <span>{htClass.students.length} / {htClass.maxStudents}</span>
              <Edit3 size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}
        </div>
        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full bg-${primaryColor}-800 rounded-full transition-all duration-500 ease-out`}
            style={{ width: `${statusPercent}%` }}
          />
        </div>
      </div>

      {/* Status Badge */}
      <div className="px-4 pb-4">
        <div
          className={`py-2 rounded-xl border flex items-center justify-center gap-2 font-black uppercase text-xs tracking-wider ${
            htClass.status === "Active"
              ? "bg-emerald-50 border-emerald-100 text-emerald-700"
              : "bg-slate-100 border-slate-200 text-slate-500"
          }`}
        >
          <div
            className={`w-2 h-2 rounded-full ${
              htClass.status === "Active"
                ? "bg-emerald-500 animate-pulse"
                : "bg-slate-400"
            }`}
          />
          {htClass.status || "Active"}
        </div>
      </div>
    </div>
  );
}